import { Device, PhysicalDeviceInput } from 'types';
import * as yup from 'yup';
import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';
import { FormModal } from 'components/form/FormModal/FormModal';
import {
    useLazyGetDeviceByDeviceIdEsQuery,
    useLazyPatchUpdateDeviceQuery,
} from 'store/services';
import { useAuth } from 'hooks/useAuth';
import { mapConsumptionTypes } from '../helpers';
import {
    CreateDeviceModalDetailsSection,
    CreateDeviceModalAddressSection,
} from '../sharedSections';
import {
    CreateDeviceModalConfirmSection,
    CreateDeviceModalGaugePictureSection,
    CreateDeviceModalInstallSection,
    CreateDeviceModalLedSection,
    CreateDeviceModalLevelCheckSection,
    CreatePhysicalDeviceModalSummarySection,
    CreateDeviceModalTankPictureSection,
    CreateDeviceModalWaitSection,
    CloneDeviceModalSection,
    CreateDeviceModalRetireDeviceSection,
} from './sections';
import {
    savePhysicalDevice,
    updateCreatePhysicalDeviceSession,
} from 'lib/firebase';
import { getGeocodeData } from 'helpers/device';

const defaultDeviceValues: PhysicalDeviceInput = {
    deviceId: '',
    fuelType: 'propane',
    gaugeType: '',
    capacity: 500,
    orientation: 'horizontal',
    consumptionType: ['heating', 'hot_water'],
    delivery_instructions: '',
    address: '',
    customer_name: '',
    name: '',
    estimatedTankLevelPct: 0,
    tankEstimateCorrect: true,
    lastReading: {},
    installAttempts: {
        gaugePicture: {
            path: '',
            url: '',
        },
        tankPicture: {
            path: '',
            url: '',
        },
    },
    group: '',
    shouldRetireDevice: null,
};

const physicalDeviceSchema = yup
    .object<{ [k in keyof PhysicalDeviceInput]: yup.AnySchema }>({
        deviceId: yup.string().required(),
        // SECTION details
        fuelType: yup.string().required(),
        gaugeType: yup.string().when('fuelType', {
            is: (fuelType: string) => fuelType === 'oil',
            then: (schema) => schema.required(),
        }),
        capacity: yup
            .number()
            .moreThan(0, 'Capacity must be greater than 0')
            .required(),
        orientation: yup.string().required(),
        // NOTE we have to convert this array into an object on form submit
        consumptionType: yup.array().min(1),
        delivery_instructions: yup.string(),
        // SECTION tank level
        estimatedTankLevelPct: yup.number().min(0).max(100).required(),
        // SECTION address
        address: yup
            .string()
            .test(
                'valid-geocode',
                'There was a problem getting the geocode for this address',
                async function (value) {
                    if (!value) {
                        return true;
                    }

                    const { error } = await getGeocodeData(value);

                    if (error) {
                        return this.createError({
                            message: error,
                        });
                    }

                    return true;
                }
            ),
        customer_name: yup.string().required(),
        name: yup.string().required(),
        group: yup.string(),
        // SECTION wait
        lastReading: yup
            .object<{
                [k in keyof PhysicalDeviceInput['lastReading']]: yup.AnySchema;
            }>({
                // FIXME this schema should be concrete and further discussed so we can decide on a concrete schema.
                time: yup.number().required(),
                tank: yup.number().required(),
                battery: yup.number(),
                temperature: yup.number(),
                voltage: yup.number(),
                insert_time: yup.number(),
                battery_warn: yup.number(),
                battery_crit: yup.number(),
                rssi: yup.number(),
                chn: yup.string(),
                rsrp: yup.string(),
                rsrq: yup.string(),
                band: yup.string(),
            })
            .required(),
        // SECTION Confirm
        tankEstimateCorrect: yup.boolean().isTrue().required(),
        // SECTION gauge and tank photos
        installAttempts: yup.object<{
            [k in keyof PhysicalDeviceInput['installAttempts']]: yup.AnySchema;
        }>({
            gaugePicture: yup.object<{
                [k in keyof PhysicalDeviceInput['installAttempts']['gaugePicture']]: yup.AnySchema;
            }>({
                url: yup.string(),
                path: yup.string(),
            }),
            tankPicture: yup.object<{
                [k in keyof PhysicalDeviceInput['installAttempts']['tankPicture']]: yup.AnySchema;
            }>({
                url: yup.string(),
                path: yup.string(),
            }),
        }),
        shouldRetireDevice: yup.boolean().nullable(),
    })
    .required();

interface CreatePhysicalDeviceModalProps extends DefaultOpenModalProps {
    newDevice: Partial<Device> | null;
    oldDevice?: Partial<Device> | null;
    supplierId?: string;
    /** When device is added use set state callback to alert parent component to refetch devices. */
    onDeviceCreated?: React.Dispatch<React.SetStateAction<boolean>>;
    /** When device is added use set state callback to alert parent component what device id was created. */
    setCreatedDeviceId?: React.Dispatch<React.SetStateAction<string>>;
}

export function CreatePhysicalDeviceModal({
    modalOpen,
    setModalOpen,
    newDevice,
    oldDevice,
    supplierId,
    onDeviceCreated,
    setCreatedDeviceId,
}: CreatePhysicalDeviceModalProps) {
    const { user } = useAuth();
    const [query] = useLazyGetDeviceByDeviceIdEsQuery();
    const [patchDeviceQuery] = useLazyPatchUpdateDeviceQuery();

    const oldDeviceId = oldDevice?.deviceId ?? '';
    const newDeviceId = newDevice?.deviceId ?? '';
    const newShortDeviceId = newDevice?.shortDeviceId ?? '';
    const internalSupplierId = supplierId ?? user?.protected?.supplierId ?? '';

    const onCloseHandler = () => {
        setModalOpen(false);
    };

    return (
        <FormModal
            defaultValues={{
                ...defaultDeviceValues,
                deviceId: newDeviceId,
            }}
            onClose={onCloseHandler}
            validationSchema={physicalDeviceSchema}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            disableFormBackButton={true}
            onValidHandler={async (deviceInput) => {
                const consumptionType = mapConsumptionTypes(deviceInput);

                // we must spread the rest of the physical device fields onto the newly created deployed device.
                const { data } = await query({
                    user,
                    deviceId: deviceInput.deviceId,
                });

                const queriedDevice = data?.results?.[0];

                const restOfDevice = queriedDevice ?? {};

                const geometry = await getGeocodeData(deviceInput.address);

                const device = {
                    ...restOfDevice,
                    ...deviceInput,
                    consumptionType,
                    protected: {
                        supplierId: internalSupplierId,
                    },
                    geometry,
                };

                if (deviceInput.deviceId) {
                    updateCreatePhysicalDeviceSession(
                        device.protected.supplierId,
                        device.deviceId,
                        device
                    );
                }

                // Set old device id to retired status if user chooses to retire the device
                if (deviceInput.shouldRetireDevice) {
                    patchDeviceQuery({
                        deviceId: oldDeviceId,
                        updatedFields: {
                            status: 'retired',
                        },
                    });
                }

                // Nullify fields that were only used as form resources and not for saving to the device object
                device.shouldRetireDevice = null;
                savePhysicalDevice(device);

                if (onDeviceCreated && setCreatedDeviceId) {
                    onDeviceCreated(true);
                    setCreatedDeviceId(device.deviceId);
                }
            }}
            title={`Configure Physical Monitor - ${newDevice?.deviceId}`}
            testId={'CreatePhysicalDeviceModal'}
        >
            {oldDevice ? (
                <ReplacePhysicalDeviceModalFlow
                    newShortDeviceId={newShortDeviceId}
                    newDeviceId={newDeviceId}
                    oldDeviceId={oldDeviceId}
                />
            ) : (
                <CreatePhysicalDeviceModalFlow
                    newShortDeviceId={newShortDeviceId}
                    newDeviceId={newDeviceId}
                />
            )}
        </FormModal>
    );
}

interface CreatePhysicalDeviceModalFlowProps {
    newDeviceId: string;
    newShortDeviceId: string;
}

function CreatePhysicalDeviceModalFlow({
    newDeviceId,
    newShortDeviceId,
}: CreatePhysicalDeviceModalFlowProps) {
    return (
        <>
            <CreateDeviceModalDetailsSection deviceId={newDeviceId} />
            <CreateDeviceModalLevelCheckSection />
            <CreateDeviceModalInstallSection />
            <CreateDeviceModalWaitSection />
            <CreateDeviceModalConfirmSection />
            <CreateDeviceModalLedSection />
            <CreateDeviceModalAddressSection />
            <CreateDeviceModalGaugePictureSection />
            <CreateDeviceModalTankPictureSection />
            <CreatePhysicalDeviceModalSummarySection
                shortDeviceId={newShortDeviceId}
            />
        </>
    );
}

interface ReplacePhysicalDeviceModalFlowProps
    extends CreatePhysicalDeviceModalFlowProps {
    newDeviceId: string;
    oldDeviceId: string;
}

function ReplacePhysicalDeviceModalFlow({
    newShortDeviceId,
    newDeviceId,
    oldDeviceId,
}: ReplacePhysicalDeviceModalFlowProps) {
    return (
        <>
            <CloneDeviceModalSection
                newDeviceId={newDeviceId}
                oldDeviceId={oldDeviceId}
            />
            <CreateDeviceModalLevelCheckSection />
            <CreateDeviceModalInstallSection />
            <CreateDeviceModalWaitSection />
            <CreateDeviceModalConfirmSection />
            <CreateDeviceModalLedSection />
            <CreateDeviceModalGaugePictureSection />
            <CreateDeviceModalTankPictureSection />
            <CreateDeviceModalRetireDeviceSection />
            <CreatePhysicalDeviceModalSummarySection
                shortDeviceId={newShortDeviceId}
            />
        </>
    );
}
